import { AlertTriangle, Coffee, Palmtree, Stethoscope, UserCheck, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";

function ChipResumen({ icon: Icon, label, value, accent, alerta }) {
  return (
    <div
      className={`rounded-xl border px-3 py-2.5 ${alerta && value > 0 ? "border-rose-300 bg-rose-50" : "border-zinc-200 bg-white"}`}
    >
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 shrink-0 ${alerta && value > 0 ? "text-rose-600" : "text-zinc-500"}`} strokeWidth={1.5} />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{label}</span>
      </div>
      <p className={`mt-1 font-display text-2xl font-light ${alerta && value > 0 ? "text-rose-700" : "text-zinc-900"}`}>
        {value}
      </p>
    </div>
  );
}

function ListaGrupo({ titulo, items, vacio, badgeClass, icon: Icon }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white/80 p-3">
      <div className="mb-2 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-zinc-500" strokeWidth={1.5} />}
        <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-600">{titulo}</h3>
        <Badge variant="outline" className={`ml-auto text-[10px] ${badgeClass}`}>
          {items.length}
        </Badge>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-zinc-400">{vacio}</p>
      ) : (
        <ul className="max-h-36 space-y-1.5 overflow-y-auto">
          {items.map((p) => (
            <li key={p.usuario_id} className="rounded-lg bg-zinc-50 px-2.5 py-2 text-sm">
              <p className="font-medium text-zinc-900">{p.nombre}</p>
              <p className="text-xs text-zinc-500">{p.detalle}</p>
              {p.departamento && <p className="text-[10px] text-zinc-400">{p.departamento}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function EstadoVivoPanel({ data }) {
  if (!data) return null;
  const r = data.resumen || {};

  return (
    <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-br from-slate-50 to-white shadow-sm" data-testid="estado-vivo-panel">
      <div className="border-b border-rose-100 bg-gradient-to-r from-rose-50 via-amber-50 to-emerald-50 px-5 py-4">
        <h2 className="font-display text-lg font-medium text-zinc-900">Estado en vivo</h2>
        <p className="mt-1 text-xs text-zinc-600">
          Situación actual del equipo · actualizado al cargar
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-6">
        <ChipResumen icon={AlertTriangle} label="Sin fichar" value={r.sin_fichar || 0} alerta />
        <ChipResumen icon={Coffee} label="En pausa" value={r.en_pausa || 0} accent="amber" />
        <ChipResumen icon={UserCheck} label="Trabajando" value={r.trabajando || 0} />
        <ChipResumen icon={Palmtree} label="Vacaciones" value={r.vacaciones || 0} />
        <ChipResumen icon={Stethoscope} label="Baja" value={r.baja || 0} />
        <ChipResumen icon={Users} label="Otros ausentes" value={r.otros_ausentes || 0} />
      </div>

      <div className="grid gap-3 px-4 pb-4 lg:grid-cols-2 xl:grid-cols-3">
        <ListaGrupo
          titulo="Deberían estar trabajando y no han picado"
          items={data.sin_fichar || []}
          vacio="Nadie pendiente de fichar en este momento"
          badgeClass="border-rose-200 text-rose-700"
          icon={AlertTriangle}
        />
        <ListaGrupo
          titulo="En pausa ahora"
          items={data.en_pausa || []}
          vacio="Nadie en pausa"
          badgeClass="border-amber-200 text-amber-800"
          icon={Coffee}
        />
        <ListaGrupo
          titulo="Vacaciones hoy"
          items={data.vacaciones || []}
          vacio="Nadie de vacaciones"
          badgeClass="border-emerald-200 text-emerald-800"
          icon={Palmtree}
        />
        <ListaGrupo
          titulo="De baja"
          items={data.baja || []}
          vacio="Nadie de baja"
          badgeClass="border-sky-200 text-sky-800"
          icon={Stethoscope}
        />
        <ListaGrupo
          titulo="Otros ausentes"
          items={data.otros_ausentes || []}
          vacio="Sin otros ausentes"
          badgeClass="border-zinc-200 text-zinc-600"
          icon={Users}
        />
        <ListaGrupo
          titulo="Trabajando ahora"
          items={data.trabajando || []}
          vacio="Nadie con jornada abierta"
          badgeClass="border-teal-200 text-teal-800"
          icon={UserCheck}
        />
      </div>
    </div>
  );
}
